import dayjs from "dayjs";
import i18next from "i18next";
import jalaliday from "jalaliday";
import { padStart, toString } from "lodash";
import { memorize } from "./utils";

const locale = i18next.language?.substring(0, 2);

dayjs.locale(locale);
dayjs.extend(jalaliday);

export const fromNow = memorize((timestamp: number = 0, fallback: string = ""): string => {
  if (timestamp === 0) return fallback;
  let difference = Math.floor(Date.now() / (timestamp.toString().length === 10 ? 1000 : 1)) - timestamp;
  const yearsDifference = Math.floor(difference / 60 / 60 / 24 / 365);
  if (yearsDifference > 0) return `${yearsDifference} ${i18next.t("date.year")} ${i18next.t("date.ago")}`;
  const monthsDifference = Math.floor(difference / 60 / 60 / 24 / 30);
  if (monthsDifference > 0) return `${monthsDifference} ${i18next.t("date.month")} ${i18next.t("date.ago")}`;
  const weeksDifference = Math.floor(difference / 60 / 60 / 24 / 7);
  if (weeksDifference > 0) return `${weeksDifference} ${i18next.t("date.week")} ${i18next.t("date.ago")}`;
  const daysDifference = Math.floor(difference / 60 / 60 / 24);
  if (daysDifference > 0) return `${daysDifference} ${i18next.t("date.day")} ${i18next.t("date.ago")}`;
  const hoursDifference = Math.floor(difference / 60 / 60);
  if (hoursDifference > 0) return `${hoursDifference} ${i18next.t("date.hour")} ${i18next.t("date.ago")}`;
  const minutesDifference = Math.floor(difference / 60);
  if (minutesDifference > 0) return `${minutesDifference} ${i18next.t("date.minute")} ${i18next.t("date.ago")}`;
  if (minutesDifference >= 0) return i18next.t("date.few_moment_ago");
  return fallback;
});

export const floatTimeFromNow = memorize((timestamp: number = 0, fallback: string = ""): string => {
  if (timestamp === 0) return fallback;
  let difference = Math.floor(Date.now() / (timestamp.toString().length === 10 ? 1000 : 1)) - timestamp;
  const time = toTime(timestamp, "", "YYYY-MM-DD");
  const daysDifference = Math.floor(difference / 60 / 60 / 24);
  if (daysDifference > 0) return `${daysDifference} ${i18next.t("date.day")} ${i18next.t("date.ago")}`;
  const hoursDifference = Math.floor(difference / 60 / 60);
  if (hoursDifference > 0) return `${hoursDifference} ${i18next.t("date.hour")} ${i18next.t("date.ago")}`;
  const minutesDifference = Math.floor(difference / 60);
  if (minutesDifference > 0) return `${minutesDifference} ${i18next.t("date.minute")} ${i18next.t("date.ago")}`;
  if (minutesDifference >= 0) return i18next.t("date.few_moment_ago");
  return time;
});

export const toTime = memorize((timestamp: number = 0, fallback: string = "", format: string = "YYYY/MM/DD HH:mm"): string => {
  if (timestamp === 0) return fallback;
  if (timestamp.toString().length === 10) timestamp *= 1000;
  return dayjs(timestamp)
    .calendar(locale === "fa" ? "jalali" : "gregory")
    .format(format);
});

export const secondToTime = (second: number): string => {
  let hours = Math.max(0, Math.floor(second / 3600));
  let minutes = Math.max(0, Math.floor((second % 3600) / 60));
  let seconds = Math.max(0, second % 60);
  let string = "";
  if (hours > 0) string += `${padStart(toString(hours), 2, "0")}:`;
  string += `${padStart(toString(minutes), 2, "0")}:`;
  string += padStart(toString(seconds), 2, "0");
  return string;
};
